import dayjs from 'dayjs';
import { marketDataGateway } from '@core/market-data-sdk';

export interface ISeries {
  id: string;
  data: any[];
  [key: string]: any;
}

interface IPeriod {
  startDate: string;
  endDate: string;
}

export class GrossUpSeries {
  private series?: ISeries[];
  private period?: IPeriod;

  constructor(series: ISeries[], period?: IPeriod) {
    this.series = series;
    this.period = period;
  }

  private needsReload(serie: ISeries): boolean {
    if (!this.period?.startDate) return false;

    const quoteStartDate = serie?.data?.[0]?.[0];
    if (!quoteStartDate) return false;

    return dayjs(this.period.startDate).diff(quoteStartDate, 'M') > 1;
  }

  private isGrossUpSeries(serie: ISeries): boolean {
    return !!serie?.id?.includes(':rfs-gu');
  }

  private async reloadSeries(serie: ISeries): Promise<ISeries> {
    if (!this.period) return serie;

    const newQuotes = await marketDataGateway.general.getQuotes({
      identifier: serie.id!,
      startDate: this.period.startDate,
      endDate: this.period.endDate,
      safe: true,
    });

    if (!newQuotes) return serie;

    return {
      ...serie,
      data: newQuotes.quotes.map(({ d, c }) => [d, c]),
    };
  }

  public shouldProcess(): boolean {
    if (!this.period?.startDate || !this.series?.length) return false;

    const grossUpSeries = this.series
      .filter((serie: ISeries) => this.isGrossUpSeries(serie))
      .filter((serie: ISeries) => this.needsReload(serie));

    return grossUpSeries.length > 0;
  }

  public async process(): Promise<ISeries[]> {
    if (!this.series) return [];

    const grossUpSeries = this.series
      .filter((serie) => this.isGrossUpSeries(serie))
      .filter((serie) => this.needsReload(serie));

    const otherSeries = this.series.filter(
      (serie) => !this.isGrossUpSeries(serie)
    );

    const updatedSeries = await Promise.all(
      grossUpSeries.map((serie) => this.reloadSeries(serie))
    );

    return [...updatedSeries, ...otherSeries];
  }
}
